import { useRef, useState } from 'react'

import { Box, Button, Checkbox } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import {
  TaskAccessIdsToEmployeeIdsDocument,
  TaskFormDocument,
  ImputableProjectForSelectFragment,
  ModalTaskFragment,
  SubprojectForSelectFragment,
} from 'generated/resources/graphql'

import { Features } from 'generated/featureDefinitions'
import { observer } from 'mobx-react'

import { useQuery } from 'modules/core/hooks/useQuery'
import DayPicker from 'modules/core/components/DayPicker'
import DropZone from 'modules/core/components/DropZone'
import FormRow from 'modules/core/components/FormRow'
import LabeledInput from 'modules/core/components/LabeledInput'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { ACCEPTED_CONTENT_TYPES, acceptedContentTypes } from 'modules/core/lib/fileFormats'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { ImputableProjectSelector } from 'modules/project_management/components/selectors/ImputableProjectSelector'
import FileList from 'modules/tasks/components/FileList'
import { useFormTaskFiles } from 'modules/tasks/hooks/useFormTaskFiles'
import { useValidateStringLength } from 'modules/tasks/hooks/useValidateStringLength'
import { TasksFiles } from 'modules/tasks/store/collections/tasks_files'
import type TaskFile from 'modules/tasks/store/models/TaskFile'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

export interface ModalTaskWithFiles extends ModalTaskFragment {
  removeFiles: TasksFiles
  newFiles: TasksFiles
  createTaskPerAsignee?: boolean
  subprojectId?: number
  projectId?: number
  employeeSelection?: EmployeeSelectorV2MultiSelectState
}

export const acceptedContentTypesByTasks = () => {
  const contentTypes = ACCEPTED_CONTENT_TYPES.filter((contentType: string): boolean => {
    const forbiddenTypes = ['zip', 'html']

    return !forbiddenTypes.some((type: string) => contentType.includes(type))
  })
  return acceptedContentTypes(contentTypes)
}

const TASK_FORM_MODE = {
  new: 'new',
  edit: 'edit',
}

interface Props {
  mode: 'new' | 'edit'
  submitting: boolean
  loading?: boolean
  readonly?: boolean
  form: FormikProps<ModalTaskWithFiles>
  close: () => void
  onSubmit: (removeFiles: TasksFiles, newFiles: TasksFiles) => void
  files: TaskFile[]
  projectSelected?: ImputableProjectForSelectFragment | null
  subprojectSelected?: SubprojectForSelectFragment | null
}

const TaskForm = ({
  mode,
  submitting,
  readonly,
  form,
  close,
  onSubmit,
  files,
  loading = false,
  projectSelected,
  subprojectSelected,
}: Props) => {
  const [selectedSubproject, setSelectedSubproject] = useState<SubprojectForSelectFragment | null>(
    subprojectSelected ?? null
  )

  const [selectedProject, setSelectedProject] = useState<ImputableProjectForSelectFragment | null>(
    projectSelected ?? null
  )

  const editorRef = useRef<EditorHandle>(null)
  const { newFiles, removeFiles, onDropFiles, onDeleteNewFile, onDeleteExistingFile } =
    useFormTaskFiles()

  const { validate, isValid } = useValidateStringLength()
  useUnsafeEffect(() => {
    validate(form.values.name)
  }, [form.values.name, validate])

  const { company } = useCurrentSession()

  const { data: featureAvailabilityData, loading: loadingFeatures } = useQuery(TaskFormDocument, {
    variables: {
      companyId: company.id,
      featureIds: [Features.projects_tasks],
    },
  })

  const hasProjectsTasks = featureAvailabilityData?.companies?.featureAvailabilitiesConnection?.nodes.find(
    (node) => node.id === Features.projects_tasks
  )?.available

  const handleProjectChanged = (value: ImputableProjectForSelectFragment | null) => {
    setSelectedProject(value)
    setSelectedSubproject(null)
    form.setFieldValue('projectId', value?.id)
  }

  const handleSubprojectChanged = (value: SubprojectForSelectFragment | null) => {
    setSelectedSubproject(value)
    form.setFieldValue('subprojectId', value?.id)
  }

  const handleEmployeeSelectionChange = (state: EmployeeSelectorV2MultiSelectState) => {
    form.setFieldValue('employeeSelection', state)
    // Also update assignees.nodes for backward compatibility with ImputableProjectSelector
    if (state.type === 'manual') {
      // For manual selection, we need to keep assignees in sync
      // Note: The actual employee data will come from the mutation response
      form.setFieldValue('assignees', {
        nodes: state.ids.map((id) => ({ employeeName: { id: null, access: { id } } })),
      })
    }
  }

  // Get default assignee IDs for the value prop (for edit mode)
  const defaultAssigneeIds =
    form.values.employeeSelection?.type === 'manual'
      ? form.values.employeeSelection.ids
      : form.values.assignees.nodes
          .map((assignee) => assignee.employeeName?.access?.id)
          .filter((id): id is number => id !== undefined && id !== null)

  const filesCount = files.length + newFiles.length
  const taskIsNew = mode === TASK_FORM_MODE.new
  const taskTitle = taskIsNew ? i18n.t('tasks.form.new.title') : i18n.t('tasks.form.edit.title')
  const formButtonTitle = taskIsNew
    ? i18n.t('tasks.form.new.submit')
    : i18n.t('tasks.form.edit.submit')
  const descriptionParams = { user: form.values.author?.fullName }
  const taskDescription = taskIsNew
    ? i18n.t('tasks.form.new.description', descriptionParams)
    : i18n.t('tasks.form.edit.description', descriptionParams)

  // Get the number of selected assignees for ImputableProjectSelector visibility
  const selectedAssigneesCount =
    form.values.employeeSelection?.type === 'manual'
      ? form.values.employeeSelection.ids.length
      : form.values.assignees.nodes.length

  // When selection is manual with useAccessId, ids are access_ids. ImputableProjectSelector
  // expects employee_id, so we resolve it via query when there's a single assignee.
  const firstAccessIdWhenManual =
    form.values.employeeSelection?.type === 'manual' &&
    form.values.employeeSelection.ids.length === 1
      ? form.values.employeeSelection.ids[0]
      : undefined

  const showProjectSelector =
    !loadingFeatures && hasProjectsTasks && selectedAssigneesCount === 1

  const { data: accessItemsData } = useQuery(  TaskAccessIdsToEmployeeIdsDocument,
    {
    variables: {
      accessIds: firstAccessIdWhenManual !== undefined ? [firstAccessIdWhenManual] : [],
    },
    skip: !showProjectSelector || firstAccessIdWhenManual === undefined,
    fetchPolicy: 'cache-first',
  })

  const firstEmployeeIdForProjectSelector =
    firstAccessIdWhenManual !== undefined
      ? accessItemsData?.employees?.employeeNamesConnection?.nodes?.[0]?.id
      : form.values.assignees.nodes[0]?.employeeName?.id

  // When manual + projects_tasks + one assignee, sync assignees with employee id from the query response
  useUnsafeEffect(() => {
    if (
      !showProjectSelector ||
      firstAccessIdWhenManual === undefined
    ) {
      return
    }
    const node = accessItemsData?.employees?.employeeNamesConnection?.nodes?.[0]
    const employeeId = node?.id
    const currentAssignee = form.values.assignees.nodes[0]
    const currentEmployeeId = currentAssignee?.employeeName?.id
    if (employeeId !== undefined && (currentEmployeeId === null || currentEmployeeId === undefined)) {
      form.setFieldValue('assignees', {
        nodes: [
          {
            employeeName: {
              id: employeeId,
              access: { id: firstAccessIdWhenManual },
            },
          },
        ],
      })
    }
  }, [
    showProjectSelector,
    firstAccessIdWhenManual,
    selectedAssigneesCount,
    accessItemsData?.employees?.employeeNamesConnection?.nodes,
    form.values.assignees.nodes,
    form,
  ])

  return (
    <Modal
      illustration="check"
      title={taskTitle}
      description={taskDescription}
      onClose={close}
      onSubmit={() => onSubmit(removeFiles, newFiles)}
      footer={
        <Button disabled={!isValid || submitting || readonly} submit>
          {formButtonTitle}
        </Button>
      }
    >
      {loading && <Loading />}
      {!loading && (
        <Box width="s400">
          <FormRow>
            <TextField
              label={i18n.t('tasks.labels.name')}
              value={form.values.name}
              onChange={(value) => form.setFieldValue('name', value)}
              readonly={readonly}
              mandatory
            />
          </FormRow>
          <FormRow>
            <LabeledInput isLegend label={i18n.t('tasks.labels.content')}>
              <TextEditor
                ref={editorRef}
                initialEditorState={{ data: (form.values.content || '').toString() ?? 'fds' }}
                placeholder={i18n.t(
                  'processes.process_settings.tasks.form.instruction_placeholder'
                )}
                height="s200"
                onChange={(e) => {
                  form.setFieldValue('content', e)
                }}
                enableBetaFeatures={false}
              />
            </LabeledInput>
          </FormRow>
          <FormRow>
            <EmployeeSelectorV2
              label={i18n.t('tasks.labels.assignees')}
              multiple
              value={defaultAssigneeIds}
              forcedFilters={{ excludeCurrentEmployee: false }}
              onChange={handleEmployeeSelectionChange}
              useAccessId
            />
            {taskIsNew && (
              <Box flexDirection="row" justifyContent="spaceBetween" paddingTop="s8">
                <Checkbox
                  size="small"
                  label={i18n.t('tasks.labels.mandatory_for_all_assignees')}
                  checked={form.values.createTaskPerAsignee}
                  onChange={(event) =>
                    form.setFieldValue('createTaskPerAsignee', event.target.checked)
                  }
                />
              </Box>
            )}
          </FormRow>
          <FormRow>
            <DayPicker
              label={i18n.t('tasks.labels.due_date')}
              value={form.values.dueOn || ''}
              readonly={!!readonly}
              onChange={(date) => {
                form.setFieldValue('dueOn', date)
              }}
              placeholder="–"
              mode="single"
              valueFormat="backend"
            />
          </FormRow>
          {showProjectSelector && (
            <FormRow>
              <ImputableProjectSelector
                value={selectedProject}
                onChange={(value) => {
                  handleProjectChanged(value)
                }}
                disabled={mode === TASK_FORM_MODE.edit}
                subprojectValue={selectedSubproject}
                onChangeSubproject={(value) => {
                  handleSubprojectChanged(value)
                }}
                subprojectOnlyActive
                forEmployeeId={firstEmployeeIdForProjectSelector}
              />
            </FormRow>
          )}
          <FormRow>
            {!readonly && <DropZone accept={acceptedContentTypesByTasks()} onDrop={onDropFiles} />}
            {filesCount > 0 && (
              <Box paddingTop="s24">
                <FileList
                  readonly={readonly}
                  currentFiles={files}
                  newFiles={newFiles.toArray()}
                  onDeleteExistingFile={onDeleteExistingFile}
                  onDeleteNewFile={onDeleteNewFile}
                />
              </Box>
            )}
          </FormRow>
        </Box>
      )}
    </Modal>
  )
}

export default observer(TaskForm)
